import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { fetchDailyUploadCount } from '../services/ai';
import { selectAccessLevel } from '../slices/subscriptions/subscriptionsSlice';
import { SubscriptionAccess } from '../types/Subscription';
import { RootState } from '../store/store';
import { resolveDocumentPageLimit } from '../utils/uploadUtils';

const FREE_PLAN_DAILY_LIMIT = 3;

export function useDailyUploadLimit() {
  const accessLevel = useSelector(selectAccessLevel);
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const isFreePlan = accessLevel === SubscriptionAccess.FREE;
  const isAmbassador = Boolean(currentUser?.ambassador);
  const isUnlimited = !isFreePlan || isAmbassador;
  const [uploadsToday, setUploadsToday] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Unknown (undefined) until /ai/uploads/today answers with the field (an
  // older backend never sends it): the PDF pre-check is skipped meanwhile and
  // the server decides, so a user with a higher per-user limit is never
  // refused in the browser.
  const [documentPageLimit, setDocumentPageLimit] = useState<
    number | undefined
  >(undefined);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchDailyUploadCount();
      setUploadsToday(data?.uploadsToday ?? 0);
      setDocumentPageLimit(resolveDocumentPageLimit(data?.documentPageLimit));
    } catch (err) {
      console.error('Failed to fetch daily upload count', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const hasReachedLimit =
    !isUnlimited &&
    isFreePlan &&
    uploadsToday !== null &&
    uploadsToday >= FREE_PLAN_DAILY_LIMIT;

  return {
    uploadsToday,
    isLoading,
    refresh,
    hasReachedLimit,
    isFreePlan,
    isUnlimited,
    accessLevel,
    limit: isUnlimited ? null : FREE_PLAN_DAILY_LIMIT,
    /** Effective per-document page cap for the PDF pre-check (ORA-761);
     *  undefined while unknown (not loaded, or not sent by the backend). */
    documentPageLimit,
  };
}
